
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, LineChart, PieChart, BarChart4 } from "lucide-react";

export function Hero() {
  const [isInView, setIsInView] = useState(false);
  
  useEffect(() => {
    setIsInView(true);
  }, []);

  return (
    <div className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24 bg-gradient-to-b from-white to-blue-50/50 dark:from-slate-950 dark:to-slate-950">
      <div className="gradient-blur"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row items-center">
          <div className="w-full lg:w-1/2 text-center lg:text-left">
            <div className={`transition-all duration-700 transform ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <span className="inline-block bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-medium py-1 px-3 rounded-full mb-4">
                The Future of Business Analytics
              </span>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white leading-tight mb-6 text-balance">
                Turn Your Data into <span className="highlight-text">Strategic Insights</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 md:max-w-lg lg:mx-0 mx-auto text-balance">
                Beautiful interactive dashboards for executives, marketers, and operators. Powered by AI for deeper insights.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <Link to="/signup">
                  <Button size="lg" className="button-shine font-medium">
                    Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/ceo-dashboard">
                  <Button variant="outline" size="lg" className="font-medium">
                    View Demo
                  </Button>
                </Link>
              </div>
              
              <div className="mt-8 inline-flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>No credit card required</span>
              </div>
            </div>
          </div>
          
          <div className="w-full lg:w-1/2 mt-12 lg:mt-0">
            <div className={`relative transition-all duration-1000 delay-300 transform ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <div className="relative p-2 bg-white dark:bg-slate-900 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-800">
                <div className="absolute inset-0 dashboard-gradient"></div>
                
                <div className="relative z-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm rounded-xl overflow-hidden p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg">Executive Dashboard</h3>
                    <div className="flex space-x-2">
                      <span className="h-3 w-3 bg-red-500 rounded-full"></span>
                      <span className="h-3 w-3 bg-yellow-500 rounded-full"></span>
                      <span className="h-3 w-3 bg-green-500 rounded-full"></span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Revenue</h4>
                        <LineChart className="h-4 w-4 text-green-500" />
                      </div>
                      <p className="text-2xl font-bold mt-2">$1.2M</p>
                      <span className="text-xs text-green-500 flex items-center">
                        <svg className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        12.5% from last month
                      </span>
                    </div>
                    
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Customers</h4>
                        <BarChart4 className="h-4 w-4 text-blue-500" />
                      </div>
                      <p className="text-2xl font-bold mt-2">8,642</p>
                      <span className="text-xs text-green-500 flex items-center">
                        <svg className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        8.3% from last month
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Revenue by Channel</h4>
                      <PieChart className="h-4 w-4 text-indigo-500" />
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className="h-3 w-3 bg-blue-500 rounded-full mr-2"></span>
                          <span className="text-xs">Direct (42%)</span>
                        </div>
                        <div className="flex items-center">
                          <span className="h-3 w-3 bg-indigo-500 rounded-full mr-2"></span>
                          <span className="text-xs">Referral (28%)</span>
                        </div>
                        <div className="flex items-center">
                          <span className="h-3 w-3 bg-purple-500 rounded-full mr-2"></span>
                          <span className="text-xs">Social (18%)</span>
                        </div>
                        <div className="flex items-center">
                          <span className="h-3 w-3 bg-pink-500 rounded-full mr-2"></span>
                          <span className="text-xs">Other (12%)</span>
                        </div>
                      </div>
                      
                      <div className="relative h-24 w-24">
                        <div className="absolute inset-0 rounded-full border-8 border-blue-500 border-r-indigo-500 border-b-purple-500 border-l-pink-500"></div>
                        <div className="absolute inset-2 bg-white dark:bg-slate-800 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="h-20 relative">
                    <div className="absolute inset-0 flex items-end">
                      <div className="w-1/12 h-3 bg-blue-200 dark:bg-blue-800/50 rounded-sm"></div>
                      <div className="w-1/12 h-5 bg-blue-300 dark:bg-blue-700/50 rounded-sm"></div>
                      <div className="w-1/12 h-7 bg-blue-400 dark:bg-blue-600/50 rounded-sm"></div>
                      <div className="w-1/12 h-12 bg-blue-500 dark:bg-blue-500/50 rounded-sm"></div>
                      <div className="w-1/12 h-16 bg-blue-600 dark:bg-blue-400/50 rounded-sm"></div>
                      <div className="w-1/12 h-12 bg-blue-500 dark:bg-blue-500/50 rounded-sm"></div>
                      <div className="w-1/12 h-8 bg-blue-400 dark:bg-blue-600/50 rounded-sm"></div>
                      <div className="w-1/12 h-14 bg-blue-500 dark:bg-blue-500/50 rounded-sm"></div>
                      <div className="w-1/12 h-10 bg-blue-400 dark:bg-blue-600/50 rounded-sm"></div>
                      <div className="w-1/12 h-6 bg-blue-300 dark:bg-blue-700/50 rounded-sm"></div>
                      <div className="w-1/12 h-9 bg-blue-400 dark:bg-blue-600/50 rounded-sm"></div>
                      <div className="w-1/12 h-4 bg-blue-200 dark:bg-blue-800/50 rounded-sm"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
